/**
 * 렌터카 검색(자동차·캠핑카·미니차량 중분류 50-03) — 목록 행·필터 축이 쓰는 순수 함수.
 *
 * 화면 구조(10-12 미리보기 확정): 4열 카드 대신 한 줄 행(사진 · 차급/이름/스펙/배지 ·
 * 1일가·기간 총액), 누르면 조건 카드가 열린다. 필터 줄 축은 차급·인승 두 개.
 *
 * 차량 스펙은 `products.specifications` 에서 읽는다. 정본 키는 아직 없어서
 * 1) `specifications.car`(앞으로 쓸 자리) → 2) 첫 `variants[].specifications`
 * (미야코 적재 형식: `specifications.miyako.variants`) 순으로 찾는다.
 * 값은 적재 원문이 섞여 있어("약 2개", "6개 이상", "미확인") 숫자·열거로 정규화하고,
 * 읽을 수 없는 값은 null 로 둔다 — 행은 null 칸을 그리지 않는다.
 */

import type { Product } from "@/types";
import { expandCategoryCodes, normalizeCategoryCodes } from "@/config/categories";

/** 자동차 소분류 — 상품 JSON-LD 의 차량 스펙 판정이 쓴다 */
export const CAR_CATEGORY_CODE = "50-03-03";
/** 렌터카 화면 중분류 — 자동차·캠핑카·미니차량(10-15: 소분류 자동차만 걸리던 것을 중분류로) */
export const CAR_GROUP_CODE = "50-03";
const CAR_GROUP_CODES = new Set(expandCategoryCodes([CAR_GROUP_CODE]));

/**
 * 고른 카테고리가 전부 50-03 중분류(그 자신 또는 하위 소분류)일 때만 렌터카 화면으로 바꾼다.
 * 다른 카테고리와 섞이면 일반 그리드. 옛 코드(50-01-01)는 별칭으로 바뀐 뒤 판정한다.
 */
export function isCarRentalSelection(codes: readonly string[]): boolean {
  return (
    codes.length > 0 &&
    normalizeCategoryCodes([...codes]).every((code) => CAR_GROUP_CODES.has(code))
  );
}

export type CarFuel = "gasoline" | "premium" | "diesel" | "hybrid" | "electric";
export type CarDrive = "2wd" | "4wd";
export type CarBags =
  | { kind: "exact"; n: number }
  | { kind: "approx"; n: number }
  | { kind: "min"; n: number };

export interface CarSpecs {
  seats: number | null;
  bags: CarBags | null;
  fuel: CarFuel | null;
  drive: CarDrive | null;
  /** 색상(세부 상품)마다 연식이 다를 수 있어 범위로 둔다. 같으면 min === max. */
  year: { min: number; max: number } | null;
}

const EMPTY_SPECS: CarSpecs = { seats: null, bags: null, fuel: null, drive: null, year: null };

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function toInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.round(value);
  if (typeof value === "string" && /^\s*\d+\s*$/.test(value)) return Number(value.trim());
  return null;
}

/** 스펙 묶음 후보 — `specifications.car` 하나, 없으면 첫 `variants` 배열의 각 `specifications`. */
function specRecords(specifications: unknown): Record<string, unknown>[] {
  const root = asRecord(specifications);
  if (!root) return [];
  const car = asRecord(root.car);
  if (car) return [car];
  for (const value of Object.values(root)) {
    const variants = asRecord(value)?.variants;
    if (!Array.isArray(variants)) continue;
    const records = variants
      .map((variant) => asRecord(asRecord(variant)?.specifications))
      .filter((record): record is Record<string, unknown> => record !== null);
    if (records.length > 0) return records;
  }
  return [];
}

export function parseCarBags(value: unknown): CarBags | null {
  const exact = toInt(value);
  if (exact !== null) return exact > 0 ? { kind: "exact", n: exact } : null;
  if (typeof value !== "string") return null;
  const match = value.match(/(\d+)/);
  if (!match) return null;
  const n = Number(match[1]);
  if (!(n > 0)) return null;
  if (/이상|以上|\+|or more/i.test(value)) return { kind: "min", n };
  if (/약|約|about|approx|~/i.test(value)) return { kind: "approx", n };
  return { kind: "exact", n };
}

export function parseCarFuel(value: unknown): CarFuel | null {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();
  if (!v) return null;
  // "고급 휘발유"가 "휘발유"보다 먼저 — 포함 관계라 순서가 결과를 바꾼다.
  if (/고급|ハイオク|premium|high.?octane/.test(v)) return "premium";
  if (/하이브리드|ハイブリッド|hybrid/.test(v)) return "hybrid";
  if (/전기|電気|electric|\bev\b/.test(v)) return "electric";
  if (/경유|軽油|ディーゼル|diesel/.test(v)) return "diesel";
  if (/휘발유|가솔린|ガソリン|レギュラー|gasoline|petrol|regular/.test(v)) return "gasoline";
  return null;
}

export function parseCarDrive(value: unknown): CarDrive | null {
  if (typeof value !== "string") return null;
  if (/4wd|awd|4x4/i.test(value)) return "4wd";
  if (/2wd|\bff\b|\bfr\b/i.test(value)) return "2wd";
  return null;
}

/** 첫 값 우선(좌석·짐·연료·구동은 색상 간 같다고 본다), 연식만 전 색상 범위. */
export function carSpecsOf(specifications: unknown): CarSpecs {
  const records = specRecords(specifications);
  if (records.length === 0) return EMPTY_SPECS;
  const first = records[0];
  const years = records
    .map((record) => toInt(record.year))
    .filter((year): year is number => year !== null && year >= 1950 && year <= 2100);
  const seats = toInt(first.seats);
  return {
    seats: seats !== null && seats > 0 ? seats : null,
    bags: parseCarBags(first.bags),
    fuel: parseCarFuel(first.fuel),
    drive: parseCarDrive(first.drive),
    year: years.length > 0 ? { min: Math.min(...years), max: Math.max(...years) } : null,
  };
}

export const CAR_CLASSES = ["compact", "van", "suv", "open", "import"] as const;
export type CarClass = (typeof CAR_CLASSES)[number];

const CAR_CLASS_TAG = "car-class:";
const OPEN_TOP = /카브리올레|컨버터블|오픈카|cabrio|convertible|roadster|カブリオレ|コンバーチブル|オープン|敞篷/i;

/**
 * 차급 — `car-class:<key>` 태그가 정본(오토바이 `bike-class:` 와 같은 방식).
 * 태그가 없으면 이름·스펙으로 추정한다: 지붕 열림 → 오픈카, 7인승 이상 → 미니밴·승합,
 * 4WD → SUV, 나머지 컴팩트. "수입 세단"은 추정하지 않는다(태그로만).
 */
export function carClassOf(
  product: Pick<Product, "name" | "tags" | "specifications">,
  specs: CarSpecs = carSpecsOf(product.specifications),
): CarClass {
  for (const tag of product.tags ?? []) {
    if (!tag.startsWith(CAR_CLASS_TAG)) continue;
    const key = tag.slice(CAR_CLASS_TAG.length) as CarClass;
    if ((CAR_CLASSES as readonly string[]).includes(key)) return key;
  }
  if (OPEN_TOP.test(product.name ?? "")) return "open";
  if (specs.seats !== null && specs.seats >= 7) return "van";
  if (specs.drive === "4wd") return "suv";
  return "compact";
}

/** 인승 축 선택지 — 데이터에 있는 최대 인승 이하만 남긴다. */
export const CAR_SEAT_STEPS = [4, 5, 7, 10] as const;

export interface CarAxes {
  classes: CarClass[];
  /** 최소 인승. null = 상관없음 */
  minSeats: number | null;
}

export const EMPTY_CAR_AXES: CarAxes = { classes: [], minSeats: null };

export function carAxesActive(axes: CarAxes): boolean {
  return axes.classes.length > 0 || axes.minSeats !== null;
}

/** 축 필터 — 인승을 모르는 차는 인승 조건이 걸리면 뺀다(조건을 만족한다고 말할 수 없다). */
export function matchesCarAxes(
  product: Pick<Product, "name" | "tags" | "specifications">,
  axes: CarAxes,
): boolean {
  if (!carAxesActive(axes)) return true;
  const specs = carSpecsOf(product.specifications);
  if (axes.classes.length > 0 && !axes.classes.includes(carClassOf(product, specs))) {
    return false;
  }
  if (axes.minSeats !== null && (specs.seats === null || specs.seats < axes.minSeats)) {
    return false;
  }
  return true;
}

/** 색상(세부 상품) 수 — 기간 티어의 sub_display_order 종류 수. 1 이하면 배지를 안 그린다. */
export function carColorCount(product: Pick<Product, "day_prices">): number {
  const subs = new Set<number>();
  for (const tier of product.day_prices ?? []) {
    subs.add(tier.sub_display_order ?? 0);
  }
  return subs.size;
}

/**
 * 색 단어 → 동그라미 색(10-13). 색 코드는 따로 저장돼 있지 않아 **이름**에서 읽는다
 * (ko·ja·en). 앞으로 파트너가 고른 값이 생기면 그것을 먼저 쓴다(`specifications.car.colors`).
 */
const COLOR_WORDS: ReadonlyArray<[RegExp, string]> = [
  [/흰|화이트|white|ホワイト|白/i, "#f5f5f4"],
  [/검정|블랙|black|ブラック|黒/i, "#1f2937"],
  [/오렌지|orange|オレンジ/i, "#f97316"],
  [/노랑|옐로|yellow|イエロー|黄/i, "#facc15"],
  [/파랑|블루|blue|ブルー|青/i, "#3b82f6"],
  [/빨강|레드|red|レッド|赤/i, "#dc2626"],
  [/은색|실버|silver|シルバー/i, "#c0c4cc"],
  [/회색|그레이|gray|grey|グレー/i, "#9ca3af"],
];

/** 이름 하나의 색 — 나온 순서로 최대 2개(투톤). 못 읽으면 []. */
function colorsOfName(name: string): string[] {
  const hits: Array<[number, string]> = [];
  for (const [re, hex] of COLOR_WORDS) {
    const match = re.exec(name);
    if (match) hits.push([match.index, hex]);
  }
  return hits.sort((a, b) => a[0] - b[0]).slice(0, 2).map(([, hex]) => hex);
}

/** 색상(세부 상품)마다 동그라미 색 — 색을 못 읽은 이름("コンパクトクラス" 등)은 뺀다. */
export function carColorsOf(specifications: unknown): string[][] {
  const root = asRecord(specifications);
  if (!root) return [];
  let names: string[] = [];
  const car = asRecord(root.car);
  if (car && Array.isArray(car.colors)) {
    names = car.colors.filter((name): name is string => typeof name === "string");
  } else {
    for (const value of Object.values(root)) {
      const variants = asRecord(value)?.variants;
      if (!Array.isArray(variants)) continue;
      names = variants
        .map((variant) => asRecord(variant)?.name)
        .filter((name): name is string => typeof name === "string");
      if (names.length > 0) break;
    }
  }
  return names.map(colorsOfName).filter((colors) => colors.length > 0);
}
